// src/pages/Switches.jsx — alta de switches
//
// La pantalla de entrada: aquí se registran los equipos con lo mínimo para
// abrir la sesión SSH (nombre, IP, puerto, usuario y contraseña), se prueba
// la conexión y se guardan. Del engrane de cada uno se entra a su vista
// (SwitchDetalle): panel y configuración de VLANs.
//
// Mismo patrón que la pantalla de OLTs, a propósito: quien ya registró una
// OLT no tiene que aprender otra forma de registrar un switch.

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Switches.css";
import { authFetch } from "../utils/authFetch";
import { Boton, EncabezadoPagina, Iconos, Insignia, Tarjeta, Vacio } from "../componentes/base";

const FORM_VACIO = {
  id: null,
  nombre: "",
  host: "",
  puerto: 22,
  usuario: "",
  password: "",
};

const ESTADOS = {
  ok: { variante: "activo", texto: "Conexión OK" },
  error: { variante: "cortado", texto: "Sin conexión" },
  "sin-probar": { variante: "neutra", texto: "Sin probar" },
};

export default function Switches() {
  const navigate = useNavigate();

  const [switches, setSwitches] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [form, setForm] = useState(FORM_VACIO);
  const [probando, setProbando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [estado, setEstado] = useState(null); // { tipo: 'exito'|'error'|'info', msg }

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await authFetch("/api/switches/");
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.status === "OK") {
        setSwitches(data.switches || []);
      } else {
        setEstado({ tipo: "error", msg: data.detalle || "No se pudo leer la lista de switches." });
      }
    } catch (e) {
      setEstado({ tipo: "error", msg: `No se pudo hablar con el servidor: ${e.message}` });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const campo = (nombre) => ({
    value: form[nombre],
    onChange: (e) => setForm((p) => ({ ...p, [nombre]: e.target.value })),
  });

  const faltantes = () => {
    const falta = [];
    if (!form.nombre.trim()) falta.push("nombre");
    if (!form.host.trim()) falta.push("IP");
    if (!form.usuario.trim()) falta.push("usuario");
    // Al editar un switch ya guardado la contraseña puede ir vacía: se
    // conserva la que ya está en la base y no se vuelve a pedir.
    if (!form.password && !form.id) falta.push("contraseña");
    return falta;
  };

  const probar = async () => {
    const falta = faltantes();
    if (falta.length) {
      return setEstado({ tipo: "error", msg: `Falta ${falta.join(", ")} para poder conectarse.` });
    }

    setProbando(true);
    setEstado({ tipo: "info", msg: `Conectando a ${form.host}:${form.puerto}…` });
    try {
      const res = await authFetch("/api/switches/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: form.id || undefined,
          host: form.host.trim(),
          puerto: Number(form.puerto) || 22,
          usuario: form.usuario.trim(),
          password: form.password,
        }),
      });
      const data = await res.json().catch(() => ({}));
      setEstado({
        tipo: data.status === "OK" ? "exito" : "error",
        msg: data.detalle || (data.status === "OK" ? "Conexión exitosa." : "No se pudo conectar."),
      });
      // Probar un switch guardado actualiza su modelo y su último estado.
      if (form.id) cargar();
    } catch (e) {
      setEstado({ tipo: "error", msg: `No se pudo hablar con el servidor: ${e.message}` });
    } finally {
      setProbando(false);
    }
  };

  const guardar = async () => {
    const falta = faltantes();
    if (falta.length) {
      return setEstado({ tipo: "error", msg: `Falta ${falta.join(", ")} para guardar.` });
    }

    setGuardando(true);
    try {
      const cuerpo = {
        nombre: form.nombre.trim(),
        host: form.host.trim(),
        puerto: Number(form.puerto) || 22,
        usuario: form.usuario.trim(),
        password: form.password,
      };
      const res = await authFetch(form.id ? `/api/switches/${form.id}` : "/api/switches/", {
        method: form.id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cuerpo),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.status === "OK") {
        setForm(FORM_VACIO);
        setEstado({ tipo: "exito", msg: form.id ? "Switch actualizado." : "Switch guardado." });
        cargar();
      } else {
        setEstado({ tipo: "error", msg: data.detalle || "No se pudo guardar." });
      }
    } catch (e) {
      setEstado({ tipo: "error", msg: `No se pudo hablar con el servidor: ${e.message}` });
    } finally {
      setGuardando(false);
    }
  };

  const editar = (sw) => {
    // La contraseña no viaja de regreso desde el servidor: se deja vacía y
    // solo se manda si quien edita escribe una nueva.
    setForm({ ...FORM_VACIO, ...sw, password: "" });
    setEstado(null);
  };

  const eliminar = async (sw) => {
    if (!window.confirm(`¿Eliminar ${sw.nombre}? Solo se borra de la app; el switch no se toca.`)) {
      return;
    }
    try {
      const res = await authFetch(`/api/switches/${sw.id}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.status === "OK") {
        if (form.id === sw.id) setForm(FORM_VACIO);
        cargar();
      } else {
        setEstado({ tipo: "error", msg: data.detalle || "No se pudo eliminar." });
      }
    } catch (e) {
      setEstado({ tipo: "error", msg: `No se pudo hablar con el servidor: ${e.message}` });
    }
  };

  return (
    <div className="sw-pagina">
      <EncabezadoPagina
        icono={Iconos.equipos}
        titulo="Switches"
        resumen="Registra un switch, prueba la conexión y entra a configurarlo."
        acciones={
          <Boton
            variante="secundario"
            icono={Iconos.agregar}
            onClick={() => { setForm(FORM_VACIO); setEstado(null); }}
            disabled={probando || guardando}
          >
            Nuevo switch
          </Boton>
        }
      />

      <Tarjeta titulo={form.id ? `Editar ${form.nombre || "switch"}` : "Nuevo switch"}>
        <div className="sw-formulario">
          <label className="sw-campo-grupo sw-campo-grupo--ancho">
            <span>Nombre</span>
            <input className="sw-campo sw-campo--lleno" placeholder="SWITCH FIBRA & WISP" {...campo("nombre")} />
          </label>

          <label className="sw-campo-grupo">
            <span>Dirección IP</span>
            <input className="sw-campo sw-campo--lleno" placeholder="10.4.1.2" {...campo("host")} />
          </label>

          <label className="sw-campo-grupo sw-campo-grupo--angosto">
            <span>Puerto SSH</span>
            <input className="sw-campo sw-campo--lleno" type="number" min={1} max={65535} {...campo("puerto")} />
          </label>

          <label className="sw-campo-grupo">
            <span>Usuario</span>
            <input className="sw-campo sw-campo--lleno" autoComplete="off" {...campo("usuario")} />
          </label>

          <label className="sw-campo-grupo">
            <span>Contraseña</span>
            <input
              className="sw-campo sw-campo--lleno"
              type="password"
              autoComplete="new-password"
              placeholder={form.id ? "(se conserva la guardada)" : ""}
              {...campo("password")}
            />
          </label>
        </div>

        <p className="sw-nota">
          Conviene una cuenta dedicada del switch, con permiso de configuración y sin caducidad de
          contraseña: si la contraseña vence, el equipo pide cambiarla al entrar y la sesión se cae.
        </p>

        {estado && (
          <p className={`sw-aviso sw-aviso--${estado.tipo === "exito" ? "exito" : estado.tipo === "error" ? "bloqueo" : "suave"}`}>
            {estado.msg}
          </p>
        )}

        <div className="sw-acciones">
          <Boton variante="secundario" icono={Iconos.actualizar} onClick={probar} cargando={probando}>
            Probar conexión
          </Boton>
          <Boton icono={Iconos.confirmar} onClick={guardar} cargando={guardando}>
            {form.id ? "Guardar cambios" : "Guardar switch"}
          </Boton>
        </div>
      </Tarjeta>

      <Tarjeta titulo="Switches registrados" sinEspacio>
        {cargando ? (
          <p className="sw-nota sw-nota--centrada">Cargando…</p>
        ) : switches.length === 0 ? (
          <Vacio
            icono={Iconos.equipos}
            titulo="Todavía no hay switches"
            descripcion="Registra el primero con el formulario de arriba."
          />
        ) : (
          <div className="sw-tabla-envoltura">
            <table className="sw-tabla">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Dirección</th>
                  <th>Usuario</th>
                  <th>Modelo</th>
                  <th>Última conexión</th>
                  <th className="sw-col-acciones" />
                </tr>
              </thead>
              <tbody>
                {switches.map((sw) => {
                  const e = ESTADOS[sw.ultimoEstado] || ESTADOS["sin-probar"];
                  return (
                    <tr key={sw.id} onClick={() => navigate(`/switches/${sw.id}`)}>
                      <td><strong>{sw.nombre}</strong></td>
                      <td className="sw-num">{sw.host}:{sw.puerto}</td>
                      <td>{sw.usuario}</td>
                      <td>{sw.modelo || "—"}</td>
                      <td>
                        <Insignia variante={e.variante} texto={e.texto} />
                        {sw.ultimaConexion && <span className="sw-negociado">{sw.ultimaConexion}</span>}
                      </td>
                      <td className="sw-col-acciones" onClick={(ev) => ev.stopPropagation()}>
                        <button
                          type="button"
                          className="sw-icono"
                          title="Abrir el switch"
                          onClick={() => navigate(`/switches/${sw.id}`)}
                        >
                          <Iconos.ajustes size={16} strokeWidth={1.9} />
                        </button>
                        <button type="button" className="sw-icono" title="Editar los datos de conexión" onClick={() => editar(sw)}>
                          <Iconos.editar size={16} strokeWidth={1.9} />
                        </button>
                        <button type="button" className="sw-icono" title="Eliminar" onClick={() => eliminar(sw)}>
                          <Iconos.eliminar size={16} strokeWidth={1.9} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>
    </div>
  );
}
